import React, { useMemo } from 'react';

import TreeMultiSelect, { isAllSelected, leafIdsOfParent } from 'CommonComponents/TreeMultiSelect';

/**
 * Adapt the `departmentsForBudget` API response into the generic tree `nodes`.
 *
 * The scope tree contains only real departments + cost centres:
 *   - one group per side (expenditure, revenue), in that order;
 *   - one parent per department (its `typed_id`);
 *   - a synthesised "No Cost Center" child (`<dep.id>::no_costcenter`) prepended
 *     for every department that has cost centres, followed by its cost centres;
 *   - departments without cost centres (e.g. general revenue departments) stay
 *     childless — they are their own leaf.
 *
 * IB-834: the two synthetic "Unreconciled" buckets that the API still appends
 * (`unreconciled_expenditure::0` / `unreconciled_revenue::0`) are filtered OUT
 * here — unallocated/unreconciled inclusion is now controlled by the dedicated
 * "Include unallocated transactions" toggle, not by the scope picker.
 */
export function buildNodes(departments) {
    if (!departments || !('expenditure' in departments) || !('revenue' in departments)) {
        return [];
    }

    const isUnreconciledBucket = (department) => typeof department.id === 'string' && department.id.startsWith('unreconciled_');

    const toParents = (list) =>
        (list || [])
            .filter((department) => !isUnreconciledBucket(department))
            .map((department) => {
                const parent = { id: department.typed_id, label: department.name };
                const costcenters = department.costcenters || [];

                if (costcenters.length > 0) {
                    parent.children = [
                        { id: `${department.id}::no_costcenter`, label: $t('No Cost Center') },
                        ...costcenters.map((cc) => ({ id: cc.typed_id, label: cc.name })),
                    ];
                }

                return parent;
            });

    return [
        { key: 'expenditure', label: $t('Expenditure'), parents: toParents(departments.expenditure) },
        { key: 'revenue', label: $t('Revenue'), parents: toParents(departments.revenue) },
    ];
}

/**
 * Fold the flat selected-leaf-id list back into the exact `{ departments, costcenters }`
 * payload the legacy form submitted.
 *
 * Equivalence with the old form + backend (`getProcessedDepartmentsIds`):
 *   - all leaves selected OR nothing selected → empty payload; `buildRequestData`
 *     then omits both keys, so the backend takes its "all departments" branch —
 *     identical to the old "All Departments" option (and to the old quirk where an
 *     empty custom selection also meant "all"). With the Unreconciled buckets no
 *     longer nodes, "all selected" now means all REAL departments + cost centres,
 *     and unallocated inclusion is decided solely by the `include_unallocated`
 *     toggle — independent of this payload;
 *   - a fully-selected department → listed in `departments` with
 *     `{ type: 'all_costcenters', ids: [] }` (backend defaults a listed department
 *     to all cost centres, so this matches the old untouched-selector behaviour);
 *   - a partially-selected department → listed in `departments` with
 *     `{ type: 'custom_costcenters', ids: [selected cc typed_ids] }` (the backend
 *     only reads a department's cost-centre entry when the department is listed);
 *   - a selected childless department (e.g. general revenue) → listed in
 *     `departments` with no cost-centre entry, exactly as before.
 */
export function toApiPayload(value, nodes) {
    if (value.length === 0 || isAllSelected(value, nodes)) {
        return { departments: [], costcenters: {} };
    }

    const selectedSet = new Set(value);
    const departments = [];
    const costcenters = {};

    (nodes || []).forEach((group) => {
        (group.parents || []).forEach((parent) => {
            const children = parent.children || [];

            if (children.length === 0) {
                if (selectedSet.has(parent.id)) {
                    departments.push(parent.id);
                }
                return;
            }

            const childIds = children.map((child) => child.id);
            const selectedChildIds = childIds.filter((id) => selectedSet.has(id));

            if (selectedChildIds.length === 0) {
                return;
            }

            departments.push(parent.id);
            costcenters[parent.id] =
                selectedChildIds.length === childIds.length ? { type: 'all_costcenters', ids: [] } : { type: 'custom_costcenters', ids: selectedChildIds };
        });
    });

    return { departments, costcenters };
}

/**
 * "3 departments, 5 cost centres selected" — whole departments counted as
 * departments, and the individually-picked cost centres of any part-selected
 * department counted separately, so the summary says what the report will cover.
 */
function summariseScope(value, nodes) {
    const selectedSet = new Set(value);
    let departments = 0;
    let costcentres = 0;

    nodes.forEach((group) => {
        group.parents.forEach((parent) => {
            const leafIds = leafIdsOfParent(parent);
            const selected = leafIds.filter((id) => selectedSet.has(id));

            if (selected.length === 0) {
                return;
            }

            if (selected.length === leafIds.length) {
                departments += 1;
                return;
            }

            costcentres += selected.length;
        });
    });

    if (costcentres === 0) {
        return $t('{{departments}} departments selected', { departments });
    }

    return $t('{{departments}} departments, {{costcentres}} cost centres selected', { departments, costcentres });
}

const chipLabel = (node, parent) => (parent ? `${parent.label} / ${node.label}` : node.label);

/**
 * Actuals-vs-Budget scope selector. Takes the raw `departmentsForBudget` response,
 * adapts it into the generic tree, and renders the reusable `TreeMultiSelect`.
 * Controlled by a flat list of selected leaf ids; the parent folds it to the API
 * payload with `toApiPayload`.
 */
export default function ReportScopeSelector({ departments, value, onChange, disabled = false }) {
    const nodes = useMemo(() => buildNodes(departments), [departments]);

    return (
        <TreeMultiSelect
            nodes={nodes}
            value={value}
            onChange={onChange}
            disabled={disabled}
            summarise={summariseScope}
            chipLabel={chipLabel}
            allLabel={$t('All departments and cost centres')}
            searchPlaceholder={$t('Search departments and cost centres…')}
        />
    );
}
